import i18next from 'i18next';
import { ARTIST_ROLE } from 'services/types';
import { IStandardEnum } from 'types/global/enum.interface';
import { Schema, mixed, number, object, string } from 'yup';

export const requiredEnumSelect = (ENUM: IStandardEnum<number>) =>
  mixed()
    .oneOf(Object.values(ENUM), () =>
      i18next.t('formik-error.option-unknown-value')
    )
    .notOneOf([-1], () => i18next.t('formik-error.option-unknown-value'))
    .required(() => i18next.t('formik-error.required'));

export const conditionalField = (
  requiredSchema: Schema,
  name = 'status',
  notRequiredValues = [-1, 1],
  notRequiredSchema?: Schema
) =>
  mixed().when(name, {
    is: (value: number) => notRequiredValues.includes(value),
    then: () => notRequiredSchema ?? mixed().notRequired(),
    otherwise: () => requiredSchema,
  });

export const dependableFields = (dependableField: string, status = 'status') =>
  conditionalField(
    mixed().required(() => i18next.t('formik-error.required')),
    status,
    [-1, 1],
    mixed().when(dependableField, {
      is: (value?: string) => !!value?.length,
      then: schema => schema.required(() => i18next.t('formik-error.required')),
      otherwise: schema => schema.notRequired(),
    })
  );

export const credensialsValidation = (statusName = 'status') =>
  object().shape({
    name: conditionalField(
      string().required(() => i18next.t('formik-error.required')),
      `$${statusName}`,
      [-1, 1],
      string().when('year', {
        is: (value?: number) => value && value !== -1,
        then: schema =>
          schema.required(() => i18next.t('formik-error.required')),
        otherwise: schema => schema.notRequired(),
      })
    ),
    year: conditionalField(
      number().required(() => i18next.t('formik-error.required')),
      `$${statusName}`,
      [-1, 1],
      number().when('name', {
        is: (value?: string) => !!value?.length,
        then: schema =>
          schema
            .not([-1], () => i18next.t('formik-error.option-unknown-value'))
            .required(() => i18next.t('formik-error.required')),
        otherwise: schema => schema.notRequired(),
      })
    ),
  });

export const titlesValidation = object().shape({
  language: number()
    .not([-1])
    .required(() => i18next.t('formik-error.required')),
  title: string().required(() => i18next.t('formik-error.required')),
});

export const externalIdsValidation = object().shape({
  id: string().required(() => i18next.t('formik-error.required')),
  type: string().required(() => i18next.t('formik-error.required')),
});

export const getTabError = (
  errors: (string | undefined)[]
): string | undefined => {
  const filteredErrors = errors.filter(value => !!value?.length);

  return filteredErrors.length ? filteredErrors[0] : undefined;
};

export const artistsValidation = object().shape({
  id: string().required(() => i18next.t('formik-error.required')),
  role: requiredEnumSelect(ARTIST_ROLE),
});

export const isEmptyId = (id?: string) =>
  id === '00000000-0000-0000-0000-000000000000';
